import { type AppConfig, appConfigSchema } from './schema';

export type ConfigIssue = { path: string; message: string };

export class ConfigError extends Error {
  readonly issues: readonly ConfigIssue[];

  constructor(issues: readonly ConfigIssue[]) {
    super(`Invalid app config: ${issues.map((i) => `${i.path}: ${i.message}`).join('; ')}`);
    this.name = 'ConfigError';
    this.issues = issues;
  }
}

/**
 * envsubst renders unset variables as "". Drop those, and drop objects that end up empty, so optional
 * sections (sentryDsn, growthbookClientKey) read as absent instead of failing validation.
 */
export const stripEmptyStrings = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stripEmptyStrings);
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
      const cleaned = stripEmptyStrings(raw);
      if (cleaned !== undefined) {
        out[key] = cleaned;
      }
    }
    return Object.keys(out).length === 0 ? undefined : out;
  }
  return value === '' ? undefined : value;
};

/** Parse runtime config at the boundary. Throws ConfigError with every problem listed, never a partial config. */
export const loadConfig = (input: unknown): AppConfig => {
  const result = appConfigSchema.safeParse(input);
  if (!result.success) {
    throw new ConfigError(
      result.error.issues.map((issue) => ({
        path: issue.path.length === 0 ? '(root)' : issue.path.map(String).join('.'),
        message: issue.message,
      })),
    );
  }
  return result.data;
};
